import { Fragment, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { Avatar, CheckboxLine, FilterSelect, HpButton, HpTag, Icon, PageHeading, TableScroll } from '@hp/ui';
import { extraBeyondRole, PERMISSIONS, ROLE_PERMISSIONS, USER_ROLES, type Permission, type UserRole } from '@hp/domain';
import { useCan, useMe } from '../api/me';
import { trpc } from '../api/trpc';
import { StatusBadge } from '../components/badges';
import { ErrorAlert } from '../components/error-alert';
import { RelativeTime } from '../components/relative-time';
import { Spinner } from '../components/ui/icons';
import { BrutTable, BrutTheadRow } from '../components/ui/table';
import { invalidateUsers, useUsers, type AdminUser } from '../features/users/queries';

const AVATAR_COLOR: Record<UserRole, 'navy' | 'green' | 'lavender'> = {
  admin: 'navy',
  member: 'green',
  viewer: 'lavender',
};

export default function AdminUsers() {
  const { t } = useTranslation(['users', 'common']);
  const me = useMe();
  const canManage = useCan()('users.manage');
  const users = useUsers(canManage);
  const [expanded, setExpanded] = useState<string | null>(null);

  const setRole = useMutation(trpc.auth.users.setRole.mutationOptions({ onSuccess: invalidateUsers }));
  const setActive = useMutation(trpc.auth.users.setActive.mutationOptions({ onSuccess: invalidateUsers }));
  const setPermissions = useMutation(trpc.auth.users.setPermissions.mutationOptions({ onSuccess: invalidateUsers }));
  const busy = setRole.isPending || setActive.isPending || setPermissions.isPending;

  if (me.isLoading) return <Spinner>{t('common:actions.loading')}</Spinner>;
  if (!canManage) return <Navigate to="/crm-data" replace />;

  function togglePermission(user: AdminUser, permission: Permission, on: boolean) {
    const extra = on ? [...user.extraPermissions, permission] : user.extraPermissions.filter((p) => p !== permission);
    setPermissions.mutate({ id: user.id, permissions: extra });
  }

  return (
    <div className="bo-sections">
      <PageHeading title={t('title')} description={t('description')} />

      <ErrorAlert error={users.error ?? setRole.error ?? setActive.error ?? setPermissions.error} />

      <section className="hp-card">
        <TableScroll label={t('tableLabel')}>
          <BrutTable>
            <BrutTheadRow>
              <th>{t('columns.user')}</th>
              <th>{t('common:table.email')}</th>
              <th>{t('columns.role')}</th>
              <th>{t('columns.permissions')}</th>
              <th>{t('common:table.status')}</th>
              <th>{t('columns.lastLogin')}</th>
              <th />
            </BrutTheadRow>
            <tbody>
              {(users.data ?? []).map((user) => {
                const isSelf = user.id === me.data?.id;
                const extras = extraBeyondRole(user.role, user.extraPermissions);
                const isOpen = expanded === user.id;
                return (
                  <Fragment key={user.id}>
                    <tr className={user.isActive ? undefined : 'bo-row--inactive'}>
                      <td>
                        <span className="bo-row">
                          <Avatar name={user.displayName ?? user.email} color={AVATAR_COLOR[user.role]} />
                          <span className="bo-strong">{user.displayName ?? '—'}</span>
                          {isSelf && <span className="bo-chip">{t('you')}</span>}
                        </span>
                      </td>
                      <td className="bo-mono">{user.email}</td>
                      <td>
                        <FilterSelect
                          value={user.role}
                          disabled={isSelf || busy}
                          title={isSelf ? t('cannotChangeOwnRole') : t(`common:roleHelp.${user.role}`)}
                          aria-label={t('roleOf', { email: user.email })}
                          onChange={(e) => setRole.mutate({ id: user.id, role: e.target.value as UserRole })}
                        >
                          {USER_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {t(`common:role.${role}`)}
                            </option>
                          ))}
                        </FilterSelect>
                      </td>
                      <td>
                        <span className="bo-row">
                          <HpTag tone={extras.length > 0 ? 'info' : 'neutral'}>
                            {extras.length > 0
                              ? t('permissionsCountExtra', {
                                  count: user.permissions.length,
                                  total: PERMISSIONS.length,
                                  extra: extras.length,
                                })
                              : t('permissionsCount', { count: user.permissions.length, total: PERMISSIONS.length })}
                          </HpTag>
                          <HpButton
                            className="bo-button--sm"
                            icon={isOpen ? 'chevronDown' : 'chevronRight'}
                            aria-expanded={isOpen}
                            onClick={() => setExpanded(isOpen ? null : user.id)}
                          >
                            {isOpen ? t('hide') : t('common:actions.edit')}
                          </HpButton>
                        </span>
                      </td>
                      <td>
                        <StatusBadge status={user.isActive ? 'active' : 'inactive'} />
                      </td>
                      <td>
                        <RelativeTime value={user.lastLoginAt} />
                      </td>
                      <td className="bo-cell-actions">
                        <HpButton
                          className={user.isActive ? 'bo-button--sm bo-danger' : 'bo-button--sm'}
                          variant={user.isActive ? 'quiet' : 'secondary'}
                          busy={setActive.isPending && setActive.variables?.id === user.id}
                          disabled={isSelf || busy}
                          title={isSelf ? t('cannotDeactivateSelf') : undefined}
                          onClick={() => setActive.mutate({ id: user.id, isActive: !user.isActive })}
                        >
                          {user.isActive ? t('deactivate') : t('activate')}
                        </HpButton>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bo-row--expanded">
                        <td colSpan={7} className="bo-cell-detail">
                          <p className="bo-hint">
                            <Icon name="info" />
                            {t('roleHint', {
                              role: t(`common:role.${user.role}`),
                              help: t(`common:roleHelp.${user.role}`),
                            })}
                          </p>
                          <div className="bo-perm-grid">
                            {PERMISSIONS.map((permission) => {
                              const fromRole = ROLE_PERMISSIONS[user.role].includes(permission);
                              return (
                                <CheckboxLine
                                  key={permission}
                                  checked={user.permissions.includes(permission)}
                                  disabled={fromRole || isSelf || busy}
                                  onChange={(e) => togglePermission(user, permission, e.target.checked)}
                                >
                                  {t(`common:permission.${permission}`)}
                                  <small className="bo-perm-grid__key"> {permission}</small>
                                </CheckboxLine>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {!users.isLoading && (users.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="bo-cell-empty">
                    {t('empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </BrutTable>
        </TableScroll>
      </section>
    </div>
  );
}
